/**
 * ECOSYSTEM_APPS — frozen registry of every Colony product surfaced in
 * the <EcosystemSwitcher> dropdown ("Products ▾") and priced on every
 * marketing site. Sourced from @puffin-core/brand-assets for the
 * canonical brand identity fields, plus a marketing-specific
 * `marketingUrl` (the public www.* domain each app's marketing site
 * lives on).
 *
 * The enum is closed: each Colony product appears exactly once. A
 * product with a brand (nine of them) is built from its brand entry; a
 * product WITHOUT one — PublishPuffin, the web-presence anchor
 * (APP-ENTITLEMENT-001 Q-AE-25; no brand entry by owner ruling
 * Q-AE-39) — takes its name from the ONE app catalog, the generic
 * puffin glyph (Q-AE-31) and the spec's blue.
 *
 * @module @puffin-core/marketing-shell/registries/ecosystemApps
 */

import { getApp } from '@puffin-core/app-catalog';
import { getBrand, marketingOriginOf } from '@puffin-core/brand-assets';

/**
 * @typedef {Object} EcosystemApp
 * @property {string} appKey                  Stable key matching brand-assets registry
 * @property {string} appName                 Display name (e.g. 'ParcelPuffin')
 * @property {Record<string,string>} taglines Per-LANGUAGE product-category line
 * @property {string} marketingUrl            Public www.* URL of the marketing site
 * @property {string} brandHex                Saturated brand color (BR-001)
 * @property {string} colonyGlyph             Path to the brand's puffin glyph (sibling-of-other-apps slot)
 */

const APP_KEYS = Object.freeze([
  'parcel',
  'publish',
  'purchase',
  'payday',
  'planner',
  'palm',
  'patrol',
  'prep',
  'port',
  'prospect',
]);

/**
 * The products the brand registry does not carry (owner ruling Q-AE-39: PublishPuffin has no brand entry — its
 * site lives in the sibling repo), with the identity fields a brand entry would have given them: the name from
 * the ONE app catalog, the generic puffin glyph (Q-AE-31, until a logo lands) and the spec's blue.
 *
 * @type {Readonly<Record<string, {taglines: Record<string, string>, brandHex: string, colonyGlyph: string}>>}
 */
const BRANDLESS_IDENTITY = Object.freeze({
  publish: Object.freeze({
    taglines: Object.freeze({
      en: 'Web presence for any business',
      es: 'Presencia web para cualquier negocio',
    }),
    brandHex: '#2563EB',
    colonyGlyph: '/branding/colony/publish.png',
    // The one origin brand-assets cannot derive: PublishPuffin has no brand entry (its site lives in the sibling
    // repo), so its marketing URL is stated here beside its other brandless facts.
    marketingUrl: 'https://www.publishpuffin.com',
  }),
});

function identityOf(appKey) {
  if (Object.hasOwn(BRANDLESS_IDENTITY, appKey)) {
    return { appName: getApp(appKey).label, ...BRANDLESS_IDENTITY[appKey] };
  }
  const brand = getBrand(appKey);
  return {
    appName: brand.appName,
    taglines: brand.taglines,
    brandHex: brand.palette.brand,
    colonyGlyph: brand.assets.colonyGlyph,
    // PALM-WEB-404-MOBILE-OVERFLOW-001 (owner ruling Q-PW404-2): brand-assets' ONE marketing-origin rule. A
    // hand-copied table had carried exactly these nine values here.
    marketingUrl: marketingOriginOf(appKey),
  };
}

function buildApp(appKey) {
  const identity = identityOf(appKey);
  return Object.freeze({
    appKey,
    appName: identity.appName,
    // AAS-I18N-002 Q-F4 — the per-LANGUAGE map travels; the RESOLVED string
    // does not. This registry is built once at module evaluation, so storing
    // a resolved tagline here would freeze every sibling product's copy in
    // whatever language the bundle happened to boot in (miss-class 1).
    // `<EcosystemSwitcher>` resolves it at render.
    taglines: identity.taglines,
    marketingUrl: identity.marketingUrl,
    brandHex: identity.brandHex,
    colonyGlyph: identity.colonyGlyph,
  });
}

/**
 * @type {Readonly<readonly EcosystemApp[]>}
 */
export const ECOSYSTEM_APPS = Object.freeze(APP_KEYS.map(buildApp));

/**
 * @param {string} appKey
 * @returns {EcosystemApp | undefined} The ecosystem entry for the given appKey, or undefined.
 */
export function findEcosystemApp(appKey) {
  return ECOSYSTEM_APPS.find((app) => app.appKey === appKey);
}
